import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { AlertTriangle, CalendarIcon } from 'lucide-react';
import { AddPartFromCatalog } from './AddPartFromCatalog';
import { JobPartsTable } from './JobPartsTable';
import { useJobParts } from '@/hooks/useJobParts';
import { useCompanyTax } from '@/hooks/useCompanyTax';
import { useCompanyLocale } from '@/hooks/useCompanyLocale';
import { useVisibility } from '@/hooks/useVisibility';
import { netFromGross, formatTaxSuffix } from '@/utils/tax';
import { partsCostTotal, partsSellTotal } from '@/utils/partsPricing';
import type { JobWithRelations } from '@/types';
import type { JobFinancials } from './CompleteJobDialog';

const financialsSchema = z.object({
  invoice_total: z.number().positive('Revenue must be greater than 0'),
  total_parts_cost: z.number().min(0, 'Parts cost cannot be negative'),
  total_parts_sell: z.number().min(0, 'Parts sell total cannot be negative'),
  toll_cost: z.number().min(0, 'Toll cost cannot be negative'),
  work_summary: z.string().max(500, 'Keep the summary under 500 characters').optional().or(z.literal('')),
  // job_date is effectively "now" and handled by the backend/hook
}).superRefine((v, ctx) => {
  // The parts lines go on the invoice at sell price and the service line absorbs the rest,
  // so the parts alone can never exceed what the customer is charged.
  if (v.total_parts_sell > v.invoice_total + 0.005) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['invoice_total'],
      message: `Parts charged to the customer (${v.total_parts_sell.toFixed(2)}) exceed the job total (${v.invoice_total.toFixed(2)}). Raise the total or lower the sell prices.`,
    });
  }
});

interface CompleteJobFinancialsStepProps {
  job: JobWithRelations;
  initialData: JobFinancials;
  onNext: (data: JobFinancials) => void;
  onCancel: () => void;
}

export function CompleteJobFinancialsStep({ job, initialData, onNext, onCancel }: CompleteJobFinancialsStepProps) {
  const { data: parts = [] } = useJobParts(job.id);
  const tax = useCompanyTax();
  const { formatMoney } = useCompanyLocale();
  const { canSeeProfit } = useVisibility();
  const { register, handleSubmit, watch, setValue, formState: { errors } } = useForm<JobFinancials>({
    resolver: zodResolver(financialsSchema),
    defaultValues: { ...initialData, job_date: new Date() }, // Default to now, though ignored by UI
  });

  const revenue = watch('invoice_total') || 0;
  const partsCost = watch('total_parts_cost') || 0;
  const partsSell = watch('total_parts_sell') || 0;
  const tollCost = watch('toll_cost') || 0;

  // Both parts totals follow the parts table (the DB trigger keeps the job row in step).
  const costFromTable = partsCostTotal(parts);
  const sellFromTable = partsSellTotal(parts);
  useEffect(() => {
    setValue('total_parts_cost', costFromTable);
    setValue('total_parts_sell', sellFromTable);
  }, [costFromTable, sellFromTable, setValue]);

  // What the customer will see on the invoice (gross figures, same basis as revenue).
  const serviceLine = revenue - partsSell;
  const partsOverTotal = revenue > 0 && partsSell > revenue + 0.005;

  // Internal calculations (cost-based, unchanged by the sell price)
  const revenueBeforeGst = netFromGross(revenue, tax);
  const jobProfitBeforeTech = revenueBeforeGst - partsCost - tollCost;
  const techCommissionPct = job.techs?.commission_percentage ?? 0;
  const techCommission = jobProfitBeforeTech * (techCommissionPct / 100);
  const companyProfit = jobProfitBeforeTech - techCommission;

  const onSubmit = (data: JobFinancials) => {
    onNext(data);
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
      <div className="space-y-4">
        <h3 className="font-semibold text-lg">Step 1: Enter Job Financials</h3>

        <div className="grid gap-4">
          <Label>Completion Date</Label>
          <div className="p-3 bg-muted rounded-md border text-sm text-muted-foreground flex items-center gap-2">
            <CalendarIcon className="h-4 w-4" />
            <span>
              Job will be closed at: <span className="font-semibold text-foreground">Current Time (Now)</span>
            </span>
          </div>
          <p className="text-xs text-muted-foreground mt-1">
            Date is automatically locked when you click Complete Job.
          </p>

          <div>
            <Label htmlFor="revenue">
              Total Job Revenue{tax.enabled ? ` (${formatTaxSuffix(tax)})` : ''} *
            </Label>
            <Input
              id="revenue"
              type="number"
              step="0.01"
              {...register('invoice_total', { valueAsNumber: true })}
              className="mt-1"
            />
            {errors.invoice_total && (
              <p className="text-sm text-destructive mt-1">{errors.invoice_total.message}</p>
            )}
          </div>

          <div>
            <Label>Parts Used</Label>
            <div className="space-y-3 mt-1">
              <AddPartFromCatalog jobId={job.id} />
              {parts.length > 0 && (
                <div className="border rounded-lg overflow-hidden">
                  <JobPartsTable parts={parts} jobId={job.id} editable />
                </div>
              )}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="parts" className="text-xs text-muted-foreground">Parts cost (internal)</Label>
                  <Input
                    id="parts"
                    type="number"
                    step="0.01"
                    {...register('total_parts_cost', { valueAsNumber: true })}
                    readOnly
                    className="bg-muted mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="parts-sell" className="text-xs text-muted-foreground">Parts charged to customer</Label>
                  <Input
                    id="parts-sell"
                    type="number"
                    step="0.01"
                    {...register('total_parts_sell', { valueAsNumber: true })}
                    readOnly
                    className="bg-muted mt-1"
                  />
                </div>
              </div>
            </div>
            {errors.total_parts_cost && (
              <p className="text-sm text-destructive mt-1">{errors.total_parts_cost.message}</p>
            )}
            {errors.total_parts_sell && (
              <p className="text-sm text-destructive mt-1">{errors.total_parts_sell.message}</p>
            )}
          </div>

          <Card className={`p-4 ${partsOverTotal ? 'border-destructive/40 bg-destructive/5' : 'bg-muted/30'}`}>
            <h4 className="text-sm font-semibold mb-2">Customer invoice preview</h4>
            {partsOverTotal ? (
              <div className="flex gap-2 items-start text-sm text-destructive">
                <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
                <p>
                  Parts charged to the customer ({formatMoney(partsSell)}) exceed the job total ({formatMoney(revenue)}).
                  Raise the total or lower the sell prices before continuing.
                </p>
              </div>
            ) : (
              <div className="space-y-1 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Service / labour</span>
                  <span>{formatMoney(Math.max(serviceLine, 0))}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Parts ({parts.length})</span>
                  <span>{formatMoney(partsSell)}</span>
                </div>
                <Separator className="my-1" />
                <div className="flex justify-between font-semibold">
                  <span>Invoice total{tax.enabled ? ` (${formatTaxSuffix(tax)})` : ''}</span>
                  <span>{formatMoney(revenue)}</span>
                </div>
                <p className="text-xs text-muted-foreground pt-1">
                  Part cost, tolls and card fees never appear on the invoice.
                </p>
              </div>
            )}
          </Card>

          <div>
            <Label htmlFor="toll">Toll Cost</Label>
            <Input
              id="toll"
              type="number"
              step="0.01"
              {...register('toll_cost', { valueAsNumber: true })}
              className="mt-1"
            />
            {errors.toll_cost && (
              <p className="text-sm text-destructive mt-1">{errors.toll_cost.message}</p>
            )}
          </div>

          <div>
            <Label htmlFor="work_summary">Work Performed (shown on invoice)</Label>
            <Textarea
              id="work_summary"
              rows={3}
              placeholder="e.g. Garage door service call — door reversing on close. Diagnosed and adjusted travel limits and safety sensors. Service completed on site."
              {...register('work_summary')}
              className="mt-1"
            />
            <p className="text-xs text-muted-foreground mt-1">
              Customer-facing description of the work. Internal job notes are never shown on the invoice.
            </p>
            {errors.work_summary && (
              <p className="text-sm text-destructive mt-1">{errors.work_summary.message}</p>
            )}
          </div>
        </div>

        {canSeeProfit && (
        <>
        <Separator />

        <Card className="p-4 bg-muted/50">
          <h4 className="font-semibold mb-3">Financial Calculations</h4>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-muted-foreground">
                {tax.enabled ? `Revenue (ex. ${tax.label}):` : 'Net Revenue:'}
              </span>
              <span className="font-medium">{formatMoney(revenueBeforeGst)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Parts margin:</span>
              <span className="font-medium">{formatMoney(partsSell - partsCost)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Job Profit before Tech:</span>
              <span className="font-medium">{formatMoney(jobProfitBeforeTech)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Tech Commission ({techCommissionPct}%):</span>
              <span className="font-medium">{formatMoney(techCommission)}</span>
            </div>
            <Separator />
            <div className="flex justify-between">
              <span className="font-semibold">Company Profit:</span>
              <span className="font-bold text-lg text-primary">{formatMoney(companyProfit)}</span>
            </div>
          </div>
        </Card>
        </>
        )}
      </div>

      <div className="flex justify-end gap-3">
        <Button type="button" variant="outline" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="submit">
          Next: Add Payments & Post
        </Button>
      </div>
    </form>
  );
}
